import React from "react";
import { useTranslation } from "react-i18next";
import Icons from "../../../Assets/Icons";
import "./PatientHistoryDetails.css";

const PatientHistoryItem = ({ historyItem, isExpanded, onToggle }) => {
    const { t } = useTranslation();

    const {
        date,
        serviceType,
        scheduledTime,
        checkInTime,
        checkOutTime,
        fee,
        staffName,
        staffId,
        patientAddress,
        woundCare,
        notes,
        userFeedback,
        nurseFeedback,
    } = historyItem;

    const patientFeedbackFields = [
        { key: "communication", label: t("communication") },
        { key: "compassionEmpathy", label: t("compassionEmpathy") },
        { key: "knowledge", label: t("knowledge") },
        { key: "professionalism", label: t("professionalism") },
        { key: "reliability", label: t("reliability") },
        { key: "skillsCompetence", label: t("skillsCompetence") },
    ];
    const staffFeedbackFields = [
        { key: "patientSatisfaction", label: t("patientSatisfaction") },
        { key: "anxietyLevel", label: t("anxietyLevel") },
        { key: "clearConnection", label: t("clearConnection") },
        { key: "culturalSensitivity", label: t("culturalSensitivity") },
        { key: "familyInvolvement", label: t("familyInvolvement") },
        { key: "treatmentAdherence", label: t("treatmentAdherence") },
    ];

    const renderStars = (rating) => {
        const normalized = Math.min(Math.max(Number(rating) || 0, 0), 5);
        return (
            <div className="phi-star-row">
                {Array.from({ length: 5 }, (_, i) => (
                    <span key={i} className={`phi-star ${i < normalized ? "filled" : ""}`}>&#9733;</span>
                ))}
            </div>
        );
    };

    const renderFeedbackColumn = (title, feedback, fields) => (
        <div className="phi-feedback-col">
            <p className="phi-feedback-role">{title}</p>
            <p className="phi-feedback-subtitle">{t("feedback")}</p>
            {feedback ? (
                <>
                    {fields.map(({ key, label }) => (
                        <div key={key} className="phi-feedback-row">
                            <span className="phi-feedback-label">{label}</span>
                            <div className="phi-feedback-rating">
                                {renderStars(feedback[key])}
                                <span className="phi-feedback-score">{feedback[key] ?? "-"}/5</span>
                            </div>
                        </div>
                    ))}
                    <div className="phi-feedback-comment">
                        <p className="phi-feedback-comment-title">{t("comment")}</p>
                        <p className="phi-feedback-comment-text">{feedback.note || t("noCommentProvided")}</p>
                    </div>
                </>
            ) : (
                <p className="phi-feedback-empty">{t("noRatingProvided")}</p>
            )}
        </div>
    );

    return (
        <div className="phi-item">
            <div className="phi-header" onClick={onToggle}>
                <div className="phi-left">
                    <div className="phi-date-icon">
                        <img src={Icons.CalenderIcon} alt="calendar" />
                    </div>
                    <div className="phi-info">
                        <p className="phi-date">{t("serviceDate")}: {date}</p>
                        <p className="phi-service">{t("serviceItem")}: {serviceType}</p>
                    </div>
                </div>
                <div className="phi-toggle">
                    <img
                        src={Icons.downArrow}
                        alt="toggle"
                        className={`phi-arrow-icon ${isExpanded ? "open" : ""}`}
                    />
                </div>
            </div>

            {isExpanded && (
                <div className="phi-content">
                    {/* Session details grid */}
                    <div className="phi-details-grid">
                        {scheduledTime && (
                            <div className="phi-detail-item">
                                <span className="phi-label">{t("sessionHours")}</span>
                                <span className="phi-value">{scheduledTime}</span>
                            </div>
                        )}
                        {checkInTime && (
                            <div className="phi-detail-item">
                                <span className="phi-label">{t("checkInTime")}</span>
                                <span className="phi-value">{checkInTime}</span>
                            </div>
                        )}
                        {checkOutTime && (
                            <div className="phi-detail-item">
                                <span className="phi-label">{t("checkOutTime")}</span>
                                <span className="phi-value">{checkOutTime}</span>
                            </div>
                        )}
                        {fee && (
                            <div className="phi-detail-item">
                                <span className="phi-label">{t("fee") || "Fee"}</span>
                                <span className="phi-value">{fee}</span>
                            </div>
                        )}
                        {staffName && (
                            <div className="phi-detail-item">
                                <span className="phi-label">{t("homecareStaff")}</span>
                                <span className="phi-value">{staffName}{staffId ? ` (${staffId})` : ""}</span>
                            </div>
                        )}
                        {patientAddress && (
                            <div className="phi-detail-item phi-detail-full">
                                <span className="phi-label">{t("serviceAddress") || "Service Address"}</span>
                                <span className="phi-value">{patientAddress}</span>
                            </div>
                        )}
                    </div>

                    {/* Observations */}
                    <div className="phi-nurse-observations">
                        <p className="phi-section-title">{t("nurseObservations")}</p>
                        {woundCare?.images?.length > 0 ? (
                            <div className="phi-images-gallery">
                                {woundCare.images.map((image, index) => (
                                    <div key={index} className="phi-image-wrapper">
                                        <img
                                            src={typeof image === "string" ? image : image?.url || image}
                                            alt={`wound care ${index + 1}`}
                                            className="phi-image"
                                        />
                                        <div className="phi-image-overlay">
                                            <img src={Icons.eye} alt="view" className="phi-eye-icon" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p style={{ fontSize: "1.3rem", color: "#888" }}>{t("noObservationAttachmentsAdded", "No observation attachments added")}</p>
                        )}
                        {woundCare?.observations && woundCare.observations !== "-" && (
                            <p className="phi-text" style={{ marginTop: "0.8rem" }}>{woundCare.observations}</p>
                        )}
                    </div>

                    {/* Notes */}
                    {(notes || woundCare?.notes) && (notes !== "-" || woundCare?.notes !== "-") && (
                        <div className="phi-notes-container">
                            <div className="phi-green-bar" />
                            <div className="phi-notes-content">
                                <p className="phi-notes-title">{t("nurseNotes")}</p>
                                <p className="phi-notes-text">{notes || woundCare?.notes}</p>
                            </div>
                        </div>
                    )}

                    {/* Feedbacks */}
                    {(userFeedback || nurseFeedback) && (
                        <div className="phi-feedback-section">
                            <p className="phi-section-title">{t("feedbacks")}</p>
                            <div className="phi-feedback-layout">
                                {renderFeedbackColumn(t("patient"), userFeedback, patientFeedbackFields)}
                                <div className="phi-feedback-divider" />
                                {renderFeedbackColumn(t("homecareStaff"), nurseFeedback, staffFeedbackFields)}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PatientHistoryItem;
